/**
 * @fileoverview Grow's loader: the calm, stepwise motion the Plant Doctor
 * waits with (owner, 2026-10-16: "when we call the plant doctor, I like that
 * motion-y loader thing ... we need to communicate that to the user in a
 * beautiful way"), as one shared piece in two sizes.
 *
 * - GrowLoader variant "screen": a screen that is loading with nothing to
 *   show yet. Its lines (`steps`) appear one by one, the finished ones
 *   ticked, the current one pulsing, and the last holds until the screen
 *   arrives. With one line (`label`) it is that line, pulsing.
 * - GrowLoader variant "block": the same lines, left-aligned in the room a
 *   section already has, for a card, a panel or a list that is loading.
 * - GrowLoader variant "inline": "Updating..." over content that is already
 *   showing: a slim card with a pulsing dot, its line and a hairline of
 *   light sliding along its foot. Quiet enough to sit at the top of a list.
 * - GrowSteps: the stepwise list itself, which the Plant Doctor's waiting
 *   state (DoctorProgress) draws its steps with, so its look is the loader's.
 *
 * Under prefers-reduced-motion nothing pulses, slides or fades in: the
 * lines still appear one by one (screen), and the inline one is a still dot
 * and line. Every loader is a polite live region.
 *
 * Used by: DoctorProgress (GrowSteps); GuidanceUpdating (inline) on Today,
 * Plants, Beds and Weather; the screens' first loads (screen).
 * Words: the caller's own, already translated.
 *
 * @module GrowLoader
 */

import React from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

/** Milliseconds between a screen loader's lines while the screen is still on its way. */
export const GROW_LOADER_STEP_MS = 1700;

/** One line of a step list. */
export interface GrowStep {
  /** A stable key for the line. */
  key: string;
  /** The line's words. */
  text: string;
  /** True once the line is ticked: it is calm and finished; the one not done pulses. */
  done: boolean;
}

/** What GrowSteps is given. */
interface GrowStepsProps {
  /** The lines showing, in order; the last not done one is the current. */
  steps: GrowStep[];
  /** Classes for the list, in place of the default padding. */
  className?: string;
}

/**
 * The stepwise list: each line fades in from just below; a finished line is
 * ticked in the theme's colour, the current one has a softly pulsing dot.
 * The look the Plant Doctor's check-up waits with.
 *
 * @param props - GrowStepsProps.
 */
export const GrowSteps: React.FC<GrowStepsProps> = ({ steps, className }) => (
  <ol role="status" aria-live="polite" className={cn('space-y-2.5 py-4 text-sm', className)}>
    {steps.map((step) => (
      <li
        key={step.key}
        className={cn(
          'flex items-center gap-3 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-1 motion-safe:duration-500',
          step.done ? 'text-foreground/80' : 'font-medium text-foreground',
        )}
      >
        <span className="flex h-5 w-5 shrink-0 items-center justify-center" aria-hidden="true">
          {step.done
            ? <Check className="h-4 w-4 text-primary" />
            : <span className="h-2.5 w-2.5 rounded-full bg-primary motion-safe:animate-pulse" />}
        </span>
        <span>{step.text}</span>
      </li>
    ))}
  </ol>
);

/** What GrowLoader is given. */
interface GrowLoaderProps {
  /** "screen" for a screen with nothing to show yet; "block" for a section of one; "inline" for an update over content already showing. */
  variant: 'screen' | 'block' | 'inline';
  /** The one line, for inline and for a screen with a single line ("Updating your garden..."). */
  label?: string;
  /** A screen's lines in order ("Checking the weather for your plants..."); they appear one by one and the last holds. */
  steps?: string[];
  /** Classes for the outer element. */
  className?: string;
}

/**
 * How many of a screen loader's lines are showing: one at first, then one
 * more every GROW_LOADER_STEP_MS until all are.
 *
 * @param count - How many lines the loader has.
 * @returns The number showing.
 */
function useRevealedCount(count: number): number {
  const [shown, setShown] = React.useState(1);
  React.useEffect(() => {
    if (count <= 1) return undefined;
    const timer = window.setInterval(() => setShown((n) => Math.min(n + 1, count)), GROW_LOADER_STEP_MS);
    return () => window.clearInterval(timer);
  }, [count]);
  return Math.min(shown, Math.max(count, 1));
}

/**
 * A screen's loader: its lines appear one by one, centred in the room the
 * screen will fill.
 *
 * @param props - The lines.
 */
const ScreenLoader: React.FC<{ lines: string[]; className?: string }> = ({ lines, className }) => {
  const shown = useRevealedCount(lines.length);
  const steps: GrowStep[] = lines.slice(0, shown).map((text, index) => ({ key: `${index}`, text, done: index < shown - 1 }));
  return (
    <div className={cn('flex min-h-[40vh] items-center justify-center px-4', className)}>
      <GrowSteps steps={steps} className="py-8" />
    </div>
  );
};

/**
 * A section's loader: its lines appear one by one, left-aligned where the
 * section's content will be.
 *
 * @param props - The lines.
 */
const BlockLoader: React.FC<{ lines: string[]; className?: string }> = ({ lines, className }) => {
  const shown = useRevealedCount(lines.length);
  const steps: GrowStep[] = lines.slice(0, shown).map((text, index) => ({ key: `${index}`, text, done: index < shown - 1 }));
  return <GrowSteps steps={steps} className={cn('py-4', className)} />;
};

/**
 * The slim "Updating..." loader: a card with a pulsing dot and its line, and
 * a hairline of light sliding along its foot.
 *
 * @param props - The line.
 */
const InlineLoader: React.FC<{ label: string; className?: string }> = ({ label, className }) => (
  <div
    role="status"
    aria-live="polite"
    className={cn(
      'relative overflow-hidden rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 text-sm text-foreground/80',
      'motion-safe:animate-in motion-safe:fade-in motion-safe:duration-500',
      className,
    )}
  >
    <span className="flex items-center gap-3">
      <span className="flex h-5 w-5 shrink-0 items-center justify-center" aria-hidden="true">
        <span className="h-2.5 w-2.5 rounded-full bg-primary motion-safe:animate-pulse" />
      </span>
      <span>{label}</span>
    </span>
    <span className="absolute inset-x-0 bottom-0 h-px bg-primary/15" aria-hidden="true">
      <span className="absolute inset-y-0 left-0 w-2/5 bg-gradient-to-r from-transparent via-primary to-transparent motion-safe:animate-grow-sweep motion-reduce:hidden" />
    </span>
  </div>
);

/**
 * Grow's loader, full for a screen or slim for an update.
 *
 * @param props - GrowLoaderProps.
 */
export const GrowLoader: React.FC<GrowLoaderProps> = ({ variant, label, steps, className }) => {
  const lines = steps && steps.length > 0 ? steps : [label ?? ''];
  if (variant === 'inline') return <InlineLoader label={label ?? lines[0]} className={className} />;
  if (variant === 'block') return <BlockLoader lines={lines} className={className} />;
  return <ScreenLoader lines={lines} className={className} />;
};

export default GrowLoader;
